import { CSSProperties, ReactElement, useMemo } from 'react';
import cc from 'classcat';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import {
  FavoritePerRow,
  FavoritePerRowMobile
} from 'utils/constants/sportsbook/magic-numbers';
import { createArray } from 'utils/helpers/create-fake-array';
import { isMobile } from 'utils/is-mobile';
import { FavoriteCasinoProviderCardSkeleton } from 'elements/Accounts/_partials/Favorites/FavoritesCasinoContent/FavoriteProviders/skeleton';
import { CasinoGameCardSkeleton } from 'newcomponents/Shared/casino/CasinoGameCard/Skeleton';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import {
  CasinoGamesLoadMoreBtnType,
  useCasinoGamesConfigsValues
} from '../../providers/CasinoGamesConfigsContext';
import { computeCasinoLoadMoreButtonType } from '../../utils/compute-configs';
import './index.less';

type TCasinoGamesByCategorySkeletonProps = {
  favProvidersEnabled?: boolean;
};
interface CustomCSSProperties extends CSSProperties {
  '--games-per-row'?: number;
}

export const CasinoGamesByCategorySkeleton = ({
  favProvidersEnabled = false
}: TCasinoGamesByCategorySkeletonProps): ReactElement => {
  const { configs, computedConfigs } = useCasinoGamesConfigsValues();
  const loadMoreButtonType = computeCasinoLoadMoreButtonType(configs);
  const { selectedCategory } = useSelectedCategory({ configs });

  const gamesByCategoryClasses = {
    aspectRatio: {
      [`casino-container__${
        configs.aspectRatio && configs.aspectRatio.toString().replace('.', '-')
      }`]: configs.aspectRatio
    }
  };

  const sliderSkeletonGridTemplate = useMemo(() => {
    if (loadMoreButtonType === CasinoGamesLoadMoreBtnType.SLIDER) {
      return {
        gridTemplateRows: `repeat(${computedConfigs.gamesRows}, auto)`,
        gridTemplateColumns: `repeat(${Math.ceil(
          computedConfigs.gamesCountForSkeleton /
            (computedConfigs.gamesRows || 1)
        )}, minmax(${computedConfigs.gamesPerRow}, auto)`,
        '--games-per-row': computedConfigs.gamesPerRow
      };
    }

    return { '--games-per-row': computedConfigs.gamesPerRow };
  }, [
    computedConfigs.gamesCountForSkeleton,
    computedConfigs.gamesRows,
    computedConfigs.gamesPerRow
  ]);

  const CategoryNameSkeleton = () => (
    <div
      className={cc([
        'x-casinoGamesByCategories__categoryName',
        { 'x-casinoGamesByCategories__categoryName--mobile': isMobile() }
      ])}
    >
      <Skeleton
        title={{
          style: { width: '150px', margin: '0', height: 24 }
        }}
        paragraph={false}
        active
      />

      {(selectedCategory === CUSTOM_CATEGORIES.all.id ||
        selectedCategory === CUSTOM_CATEGORIES.favorite.id) && (
        <div className="x-casinoGamesByCategories__categoryName__viewAll">
          <Skeleton
            title={{
              style: { width: '100px', margin: '0', height: 20 }
            }}
            paragraph={false}
            active
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cc([
        'x-casinoGamesByCategories',
        gamesByCategoryClasses.aspectRatio,
        {
          'x-casinoGamesByCategories__slider':
            loadMoreButtonType === CasinoGamesLoadMoreBtnType.SLIDER
        }
      ])}
    >
      {favProvidersEnabled && (
        <div
          className={cc([
            'favoritesCasinoProviders__container--skeleton',
            {
              'favoritesCasinoProviders__container--skeleton--mobile':
                isMobile()
            }
          ])}
        >
          {computedConfigs.categoryNameVisible &&
            configs.groupAllByCategories && <CategoryNameSkeleton />}
          <div
            className="x-casinoGamesByCategories__providers favoritesCasinoProviders"
            style={
              {
                ...sliderSkeletonGridTemplate,
                '--games-per-row': isMobile()
                  ? FavoritePerRowMobile
                  : FavoritePerRow
              } as CustomCSSProperties
            }
          >
            {Array.from({ length: 3 }, (v, i) => i).map(provider => (
              <FavoriteCasinoProviderCardSkeleton key={provider} />
            ))}
          </div>
        </div>
      )}

      {computedConfigs.categoryNameVisible && <CategoryNameSkeleton />}

      <div
        className="x-casinoGamesByCategories__games x-casinoGamesByCategories__games--skeleton"
        style={sliderSkeletonGridTemplate}
      >
        {createArray(computedConfigs.gamesCountForSkeleton).map(key => (
          <CasinoGameCardSkeleton key={key} />
        ))}
      </div>
    </div>
  );
};
